import colors from '@constants/colors';
import styled from '@emotion/styled';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { memo } from 'react';

import { MapProps } from '../types';
import useSupercluster from './hooks/useSupercluster';

function ClusteredMarkers({ markers, onMarkerClick }: Pick<MapProps, 'markers' | 'onMarkerClick'>) {
    const { clusters } = useSupercluster(markers);

    return (
        <>
            {clusters.map((cluster) => {
                const [lng, lat] = cluster.geometry.coordinates;
                const position = { lat, lng };

                if (cluster.properties.cluster) {
                    const { point_count: count } = cluster.properties;
                    return (
                        <AdvancedMarker key={`cluster-${cluster.id}`} position={position}>
                            <ClusterIcon count={count}>{count}</ClusterIcon>
                        </AdvancedMarker>
                    );
                }

                const {
                    id, name, isMyShop,
                } = cluster.properties;
                return (
                    <AdvancedMarker
                        key={id}
                        position={position}>
                        <MarkerIcon onClick={() => onMarkerClick?.(id, lat, lng)}>
                            {isMyShop && <HeartIcon />}
                            {name}
                        </MarkerIcon>
                    </AdvancedMarker>
                );
            })}
        </>
    );
}

function getClusterSize(count: number): number {
    if (count < 10) return 40;
    if (count < 50) return 55;
    return 74;
}

const ClusterIcon = styled.div<{ count: number }>`
    align-items: center;
    background: rgba(110, 104, 100, 0.8);
    border-radius: 50%;
    color: var(--color-white);
    cursor: pointer;
    display: flex;
    font-size: 14px;
    font-weight: bold;
    height: ${({ count }) => getClusterSize(count)}px;
    justify-content: center;
    width: ${({ count }) => getClusterSize(count)}px;
`;

const MarkerIcon = styled.div`
    align-items: center;
    background-color: var(--color-gray6);
    border-radius: 15px;
    color: var(--color-white);
    column-gap: 4px;
    display: flex;
    font-size: 14px;
    font-weight: bold;
    height: 32px;
    line-height: 20px;
    padding: 0 10px;
`;

function HeartIcon() {
    return <svg width="20" height="20" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd"
            d="M14 21.75C12.0017 21.315 5.84375 17.9075 5.84375 11.7812C5.84375 10.8302 6.14298 9.9033 6.69906 9.13178C7.25514 8.36027 8.03987 7.78327 8.94209 7.48253C9.84432 7.18179 10.8183 7.17254 11.7261 7.45611C12.6338 7.73968 13.4294 8.30168 14 9.0625C14.5706 8.30168 15.3662 7.73968 16.2739 7.45611C17.1817 7.17254 18.1557 7.18179 19.0579 7.48253C19.9601 7.78327 20.7449 8.36027 21.3009 9.13178C21.857 9.9033 22.1562 10.8302 22.1562 11.7812C22.1562 17.9075 15.9983 21.315 14 21.75Z"
            fill={colors.gray5} stroke="white" strokeWidth="1.93333"
            fillOpacity="0.7"/>
    </svg>;
}

export default memo(ClusteredMarkers, (prev, next) => {
    if (prev.markers === next.markers) return true;
    if (prev.markers?.length !== next.markers?.length) return false;
    return prev.markers!.every((m, i) => {
        const n = next.markers![i];
        return m.id === n.id && m.lat === n.lat && m.lng === n.lng && m.name === n.name && m.isMyShop === n.isMyShop;
    });
});
